import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Tooltip, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import ToastMsg from "../../Components/ToastMsg/ToastMsg";
import { DeletePopup, CommonGridHeader } from "../../Components/Common Components/CommonComponents";
import ImagePaths from "../../Utility/ImagePaths";
import RestAPIService from "../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../Utility/API/Serviceurls";
import { Colors } from "../../Styles/Colors";
import { CheckAccessFunc } from "../../Components/Common Components/DashboardComponents";
import "../../Styles/Configuration.css";

export default function ResultTemplate() {
  const navigate = useNavigate();
  const [serviceTemplateData, setServiceTemplateData] = useState([]);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [removeData, setRemoveData] = useState(false);
  const [deletePopup, setDeletePopup] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [templateName, setTemplateName] = useState("");
  const [isLoader, setLoader] = useState(false);
  const [isErrorMsg, setIsErrorMsg] = useState(false);
  const [isErrorMsgText, setIsErrorMsgText] = useState("");
  const [successMsg, setSuccessMsg] = useState(false);
  const [successMsgText, setSuccessMsgText] = useState("");

  const getTestTemplateData = useCallback(() => {
    try {
      setLoader(true);
      RestAPIService.getAll(Serviceurls.HISTOPATHOLOGY_RESULT_TEMPLATE)
        .then((response) => {
          if (response.data.status === "success") {
            setServiceTemplateData(response.data.data ?? []);
          }
          setLoader(false);
        })
        .catch((error) => {
          setIsErrorMsg(true);
          setIsErrorMsgText(error?.response?.data?.message ?? error.message);
          setLoader(false);
        });
    } catch (error) {
      setIsErrorMsg(true);
      setIsErrorMsgText(error.message);
      setLoader(false);
    }
  }, []);

  useEffect(() => {
    getTestTemplateData();
  }, [getTestTemplateData]);

  const deleteServiceTemplateData = useCallback(() => {
    try {
      setRemoveData(true);
      RestAPIService.delete(Serviceurls.HISTOPATHOLOGY_RESULT_TEMPLATE + "?template_id=" + selectedId)
        .then((response) => {
          if (response.data.status === "success") {
            successMessage(response.data.message);
            getTestTemplateData();
            setRemoveData(false);
            setSelectedId(null);
            setDeletePopup(false);
          }
        })
        .catch((error) => {
          setRemoveData(false);
          errorMessage(error?.response?.data?.message ?? error.message);
        });
    } catch (error) {
      setRemoveData(false);
      errorMessage(error.message);
    }
  }, [selectedId, getTestTemplateData]);

  const errorMessage = (message) => {
    setIsErrorMsg(true);
    setIsErrorMsgText(message);
  };

  const successMessage = (message) => {
    setSuccessMsg(true);
    setSuccessMsgText(message);
  };

  const msgClose = () => {
    setIsErrorMsg(false);
    setIsErrorMsgText("");
    setSuccessMsg(false);
    setSuccessMsgText("");
  };

  const popupClose = () => setDeletePopup(false);

  const onDeleteHandler = (item) => {
    setDeletePopup(true);
    setTemplateName(item?.template_name ?? "");
    setSelectedId(item?.id);
  };

  const editAccess = CheckAccessFunc("Histopathology", "Settings", "Result Template", null, "Tab")?.editAccess;

  const rows = (serviceTemplateData ?? []).map((el, index) => ({ ...el, sno: index + 1 }));

  const baseColumns = [
    { field: "sno", headerName: "S.No", flex: 0.06, align: "center", headerAlign: "center", renderCell: (params) => <Box component="div">{params?.row?.sno}</Box> },
    {
      field: "template_name",
      headerName: "Template Name",
      flex: 0.3, align: "left", headerAlign: "left",
          renderCell: (params) => (
            <Box>
              {params?.row?.template_name ? (
                params?.row?.template_name?.length > 40 ? (
                  <Tooltip placement="top" title={params?.row?.template_name} arrow>
                    <Box>{params?.row?.template_name?.slice(0, 40) + "..."}</Box>
                  </Tooltip>
                ) : (
                  <Box>{params?.row?.template_name}</Box>
                )
              ) : (
                "-"
              )}
            </Box>
          ),
        },
        {
          field: "service_head",
          headerName: "Service Head",
          flex: 0.3,
          renderCell: (params) => (
            <Box component="div">
              {params?.row?.service_head ? (
                params?.row?.service_head?.length > 40 ? (
                  <Tooltip placement="top" title={params?.row?.service_head} arrow>
                    <Box>{params?.row?.service_head?.slice(0, 40) + "..."}</Box>
                  </Tooltip>
                ) : (
                  <Box>{params?.row?.service_head}</Box>
                )
              ) : (
                "-"
              )}
            </Box>
          ),
        },
        {
          field: "service_name",
          headerName: "Service Name",
          flex: 0.24,
          renderCell: (params) => (
            <Box component="div">
              {params?.row?.service_name ? (
                params?.row?.service_name?.length > 35 ? (
                  <Tooltip placement="top" title={params?.row?.service_name} arrow>
                    <Box>{params?.row?.service_name?.slice(0, 35) + "..."}</Box>
                  </Tooltip>
                ) : (
                  <Box>{params?.row?.service_name}</Box>
                )
              ) : (
                "-"
              )}
            </Box>
          ),
        },
      ];

  const actionColumn = {
    field: "action",
    headerName: "Action",
    flex: 0.1,
    align: "center",
    headerAlign: "center",
    renderCell: (params) => (
      <Box component="div" display="flex" justifyContent="center">
        <Button
          className="Common_Btn_Min_Width"
          disabled={!editAccess}
          onClick={() => navigate("/HistopathologySetting/MasterConfiguration/ResultTemplate/Add", { state: { templateData: params?.row } })}
        >
          <Box component="img" src={ImagePaths.LabEditButton?.default ?? ImagePaths.LabEditButton} height="1.5vw" width="1.5vw" alt="Edit" />
        </Button>
        <Button className="Common_Btn_Min_Width" onClick={() => onDeleteHandler(params?.row)}>
          <Box component="img" src={ImagePaths.Delete?.default ?? ImagePaths.Delete} height="1.5vw" width="1.5vw" alt="Delete" />
        </Button>
      </Box>
    ),
  };

  const columns = editAccess ? [...baseColumns, actionColumn] : baseColumns;

  return (
    <Box component="div" sx={{ width: "100%", boxSizing: "border-box" }}>
      <Box component="div" className="rad_tst_container" sx={{ width: "100%", backgroundColor: Colors.Background, boxSizing: "border-box" }}>
        <Box component="div" display="flex" justifyContent="flex-end" alignItems="center">
          <Button
            variant="contained"
            sx={{ textTransform: "capitalize", height: "2vw" }}
            disabled={!editAccess}
            onClick={() => navigate("/HistopathologySetting/MasterConfiguration/ResultTemplate/Add")}
          >
            Add New Template
          </Button>
        </Box>
        <Box component="div" height="71vh" mt="0.5vw" sx={{ width: "100%" }}>
          <DataGrid
            sx={{
              width: "100%",
              backgroundColor: Colors.white,
              "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 600, color: "#616161", fontSize: "0.9dvw" },
              "& .MuiDataGrid-columnHeader": { backgroundColor: "#F5F8F7" },
              "& .MuiDataGrid-cell": { fontSize: "0.9dvw" },
            }}
            columns={columns}
            rows={rows}
            getRowId={(row) => row.id ?? row.sno}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            hideFooterSelectedRowCount
            headerHeight={40}
            rowHeight={40}
            showToolbar
            localeText={{
              toolbarColumns: "",
              toolbarDensity: "",
              toolbarExport: "",
              toolbarFilters: "",
              toolbarExportPrint: "",
            }}
            slots={{
              toolbar: CommonGridHeader.CommonToolBar,
              noRowsOverlay: () => (
                <Box className="eMed_conf_nodata">No Records To Be Shown</Box>
              ),
            }}
            pageSizeOptions={[10, 20, 30]}
            disableRowSelectionOnClick
            pagination
            loading={isLoader}
          />
        </Box>
      </Box>
      {isErrorMsg && <ToastMsg severity="error" msg={isErrorMsgText} msgPop={msgClose} />}
      {successMsg && <ToastMsg severity="success" msg={successMsgText} msgPop={msgClose} />}
      {deletePopup && (
        <DeletePopup
          DeletTitle={`Are you sure you want to delete template - ${templateName} ?`}
          deleteAlertPopupClose={popupClose}
          removeData={deleteServiceTemplateData}
          DeleteNotify="It will affect the entire Configuration"
          disable={removeData}
        />
      )}
    </Box>
  );
}
